import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  approveAgentPlanApi,
  getAgentEventsApi,
  getAgentRunApi,
  pauseAgentRunApi,
  resumeAgentRunApi,
  startAgentRunApi,
  stopAgentRunApi,
  streamAgentEventsApi
} from '../../api/agent.api';
import {
  appendAgentActivity,
  ensureAgentStreamingPlaceholder,
  fetchChatMessages,
  finalizeAgentAssistant,
  streamDeltaReceived
} from './chatSlice';
import { fetchProjectById } from './projectsSlice';
import { runProjectSandbox } from './previewSlice';
import { fetchWalletBalance } from './walletSlice';

const STORAGE_KEY = 'risman.activeAgentRun';

/** AbortController for the active SSE watch (not serializable in Redux). */
let activeStreamController = null;

const TERMINAL_EVENT_TYPES = new Set(['run.completed', 'run.failed', 'run.stopped']);

const ACTIVITY_EVENT_TYPES = new Set([
  'run.started',
  'plan.created',
  'plan.waiting_approval',
  'plan.approved',
  'tool.started',
  'tool.finished',
  'file.written',
  'reflection',
  'critic',
  'step.started',
  'run.paused'
]);

const saveActiveRun = (run, afterSeq = 0) => {
  try {
    if (!run) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        runId: run.id,
        chatId: run.chatId,
        projectId: run.projectId,
        afterSeq
      })
    );
  } catch {
    // ignore
  }
};

export const loadActiveRunMeta = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
};

export const abortAgentWatch = () => {
  if (activeStreamController) {
    activeStreamController.abort();
    activeStreamController = null;
  }
};

const toRejectValue = (error) => {
  const data = error?.response?.data;
  const message =
    (typeof data === 'string' && data) ||
    data?.message ||
    data?.Message ||
    data?.title ||
    data?.Title ||
    error?.message ||
    'Agent request failed.';

  return {
    ...(data && typeof data === 'object' ? data : {}),
    message,
    statusCode: error?.response?.status
  };
};

const parsePayload = (payloadJson) => {
  try {
    return typeof payloadJson === 'string' ? JSON.parse(payloadJson || '{}') : payloadJson || {};
  } catch {
    return {};
  }
};

const getAgentStreamingContent = (state, chatId) => {
  const messages = state.chat.byChatId[chatId] || [];
  const assistant = [...messages]
    .reverse()
    .find(
      (item) =>
        item.role === 'assistant' &&
        item.isStreaming &&
        String(item.requestId || '').startsWith('agent-')
    );
  return assistant?.content || assistant?.message || '';
};

const typewriterToContent = (dispatch, chatId, fullText, fromLength = 0) =>
  new Promise((resolve) => {
    if (!fullText || fromLength >= fullText.length) {
      dispatch(streamDeltaReceived({ chatId, content: fullText || '' }));
      resolve();
      return;
    }

    let index = fromLength;
    const step = () => {
      index = Math.min(fullText.length, index + 4);
      dispatch(streamDeltaReceived({ chatId, content: fullText.slice(0, index) }));
      if (index >= fullText.length) {
        resolve();
        return;
      }
      setTimeout(step, 14);
    };
    step();
  });

export const startAgentRun = createAsyncThunk('agent/start', async (payload, { rejectWithValue }) => {
  try {
    const run = await startAgentRunApi(payload);
    saveActiveRun(run);
    return run;
  } catch (error) {
    return rejectWithValue(toRejectValue(error));
  }
});

export const reconnectAgentRun = createAsyncThunk(
  'agent/reconnect',
  async ({ runId, afterSeq = 0 }, { rejectWithValue }) => {
    try {
      const [run, events] = await Promise.all([getAgentRunApi(runId), getAgentEventsApi(runId, afterSeq)]);
      return { run, events };
    } catch (error) {
      return rejectWithValue(toRejectValue(error));
    }
  }
);

export const pauseAgentRun = createAsyncThunk('agent/pause', async (runId) => {
  await pauseAgentRunApi(runId);
  return runId;
});

export const resumeAgentRun = createAsyncThunk('agent/resume', async (runId, { dispatch, getState }) => {
  await resumeAgentRunApi(runId);
  const afterSeq = getState().agent.afterSeq || 0;
  void dispatch(watchAgentRun(runId, afterSeq));
  return runId;
});

export const stopAgentRun = createAsyncThunk('agent/stop', async (runId) => {
  abortAgentWatch();
  await stopAgentRunApi(runId);
  return runId;
});

export const approveAgentPlan = createAsyncThunk(
  'agent/approvePlan',
  async ({ runId, editedPlanJson }, { dispatch, getState }) => {
    await approveAgentPlanApi(runId, editedPlanJson);
    const afterSeq = getState().agent.afterSeq || 0;
    void dispatch(watchAgentRun(runId, afterSeq));
    return runId;
  }
);

const initialState = {
  mode: 'agent', // ask | plan | agent
  activeRun: null,
  events: [],
  afterSeq: 0,
  streaming: false,
  error: null
};

const applyEventSideEffects = (state, evt) => {
  if (!evt?.type) {
    return;
  }

  if (state.activeRun) {
    if (evt.type === 'plan.waiting_approval') {
      state.activeRun.status = 3;
      state.streaming = false;
    } else if (evt.type === 'run.paused') {
      state.activeRun.status = 4;
      state.streaming = false;
    } else if (
      evt.type === 'run.started' ||
      evt.type === 'plan.created' ||
      evt.type === 'message.delta' ||
      evt.type?.startsWith('tool.')
    ) {
      state.streaming = true;
      if (evt.type === 'run.started') {
        state.activeRun.status = 2;
      }
    } else if (evt.type === 'run.completed') {
      // Keep streaming true until typewriter/finalize finishes in watchAgentRun.
      state.activeRun.status = 5;
    } else if (evt.type === 'run.failed') {
      state.streaming = false;
      state.activeRun.status = 6;
    } else if (evt.type === 'run.stopped') {
      state.streaming = false;
      state.activeRun.status = 7;
    }
  } else if (evt.type === 'run.failed' || evt.type === 'run.stopped') {
    state.streaming = false;
  }
};

const agentSlice = createSlice({
  name: 'agent',
  initialState,
  reducers: {
    setAgentMode(state, action) {
      state.mode = action.payload;
    },
    agentEventReceived(state, action) {
      const evt = action.payload;
      if (!evt || state.events.some((e) => e.seq === evt.seq && e.id === evt.id)) {
        return;
      }
      state.events.push(evt);
      if (evt.seq > state.afterSeq) {
        state.afterSeq = evt.seq;
      }
      applyEventSideEffects(state, evt);
      try {
        const meta = loadActiveRunMeta();
        if (meta) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...meta, afterSeq: state.afterSeq }));
        }
      } catch {
        // ignore
      }
    },
    setAgentStreaming(state, action) {
      state.streaming = Boolean(action.payload);
    },
    clearAgentRun(state) {
      state.activeRun = null;
      state.events = [];
      state.afterSeq = 0;
      state.streaming = false;
      saveActiveRun(null);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(startAgentRun.pending, (state) => {
        state.streaming = true;
        state.error = null;
        state.events = [];
        state.afterSeq = 0;
      })
      .addCase(startAgentRun.fulfilled, (state, action) => {
        state.activeRun = action.payload;
        state.streaming = true;
      })
      .addCase(startAgentRun.rejected, (state, action) => {
        state.streaming = false;
        state.error = action.payload || 'failed';
      })
      .addCase(reconnectAgentRun.fulfilled, (state, action) => {
        state.activeRun = action.payload.run;
        state.events = action.payload.events;
        state.afterSeq = action.payload.events.reduce((max, e) => Math.max(max, e.seq || 0), 0);
        const status = Number(action.payload.run.status);
        state.streaming = status === 1 || status === 2;
        action.payload.events.forEach((evt) => applyEventSideEffects(state, evt));
      })
      .addCase(pauseAgentRun.fulfilled, (state) => {
        state.streaming = false;
        if (state.activeRun) {
          state.activeRun.status = 4;
        }
      })
      .addCase(resumeAgentRun.fulfilled, (state) => {
        state.streaming = true;
        if (state.activeRun) {
          state.activeRun.status = 2;
        }
      })
      .addCase(approveAgentPlan.fulfilled, (state) => {
        state.streaming = true;
        if (state.activeRun) {
          state.activeRun.status = 2;
        }
      })
      .addCase(stopAgentRun.fulfilled, (state) => {
        state.streaming = false;
        if (state.activeRun) {
          state.activeRun.status = 7;
        }
      });
  }
});

export const { setAgentMode, agentEventReceived, setAgentStreaming, clearAgentRun } = agentSlice.actions;
export default agentSlice.reducer;

const finishAgentRunSideEffects = async (dispatch, run, evtType, finalMessage = '') => {
  const chatId = run?.chatId;

  if (chatId) {
    dispatch(finalizeAgentAssistant({ chatId, reply: finalMessage || undefined }));
    await dispatch(fetchChatMessages({ chatId }));
  }

  if (run?.projectId && evtType === 'run.completed') {
    try {
      await dispatch(fetchProjectById(run.projectId)).unwrap();
    } catch (error) {
      console.warn(error?.message || 'Project refresh failed.');
    }
    try {
      await dispatch(runProjectSandbox({ projectId: run.projectId })).unwrap();
    } catch (error) {
      console.warn(error?.message || 'Sandbox preview failed.');
    }
  }

  dispatch(fetchWalletBalance());
  dispatch(setAgentStreaming(false));

  if (TERMINAL_EVENT_TYPES.has(evtType)) {
    saveActiveRun(null);
  }
};

const handleAgentChatEvent = (dispatch, getState, evt) => {
  const run = getState().agent.activeRun;
  const chatId = run?.chatId;
  if (!chatId) {
    return;
  }

  const payload = parsePayload(evt.payloadJson);

  if (evt.type === 'message.delta') {
    const full = payload.full || payload.Full || '';
    if (full) {
      dispatch(streamDeltaReceived({ chatId, content: full }));
    }
    return;
  }

  if (ACTIVITY_EVENT_TYPES.has(evt.type)) {
    dispatch(
      appendAgentActivity({
        chatId,
        type: evt.type,
        payload,
        seq: evt.seq,
        createdAt: evt.createdAt
      })
    );
  }
};

export const applyAgentEventsToChat = (events) => (dispatch, getState) => {
  const run = getState().agent.activeRun;
  if (run?.chatId) {
    dispatch(ensureAgentStreamingPlaceholder(run.chatId));
  }
  (events || []).forEach((evt) => {
    handleAgentChatEvent(dispatch, getState, evt);
  });
};

export const watchAgentRun = (runId, afterSeq = 0) => async (dispatch, getState) => {
  abortAgentWatch();
  const controller = new AbortController();
  activeStreamController = controller;

  try {
    await streamAgentEventsApi({
      runId,
      afterSeq,
      signal: controller.signal,
      onEvent: (evt) => {
        const alreadySeen = getState().agent.events.some((e) => e.seq === evt.seq && e.id === evt.id);
        dispatch(agentEventReceived(evt));
        if (alreadySeen) {
          return;
        }

        handleAgentChatEvent(dispatch, getState, evt);

        if (TERMINAL_EVENT_TYPES.has(evt.type)) {
          const run = getState().agent.activeRun;
          const chatId = run?.chatId;
          const payload = parsePayload(evt.payloadJson);

          void (async () => {
            if (evt.type === 'run.completed' && chatId) {
              const message = payload.message || payload.Message || '';
              const current = getAgentStreamingContent(getState(), chatId);
              if (message && current.length < message.length) {
                await typewriterToContent(dispatch, chatId, message, current.length);
              } else if (message && !current) {
                await typewriterToContent(dispatch, chatId, message, 0);
              }
              await finishAgentRunSideEffects(dispatch, run, evt.type, message);
            } else {
              const errText = payload.error || payload.reason || '';
              await finishAgentRunSideEffects(dispatch, run, evt.type, errText);
            }

            if (activeStreamController === controller) {
              activeStreamController = null;
            }
          })();
        }
      }
    });
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.warn(error);
    }
  } finally {
    if (activeStreamController === controller) {
      activeStreamController = null;
    }
  }

  return controller;
};
